/**
 * EnterpriseSchemas — Global JSON-LD structured data
 * Organization, LocalBusiness (3 offices), WebSite+SearchAction, Service, FAQPage
 * Injected once via MainLayout for all pages.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL } from '@/lib/seo';
import BRAND from '@/config/brand';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { normalizeSchemaLogos } from '@/lib/schema-normalize';

function injectSchemas(schemas: Record<string, unknown>[]) {
  // 1. Remove previous dynamic injections
  document.head.querySelectorAll('script[data-seo="enterprise-jsonld"]').forEach(el => el.remove());
  
  // 2. Remove static JSON-LD from index.html to prevent duplicates
  // Static scripts don't have data-seo attribute, so we target unattributed JSON-LD scripts
  // Scope to <head> ONLY: JSON-LD rendered inside <body> may be a React-owned
  // node, and removing it corrupts React's DOM references (white-screen crash
  // with "insertBefore: node is not a child of this node").
  document.head.querySelectorAll('script[type="application/ld+json"]:not([data-seo])').forEach(el => el.remove());
  
  for (const rawSchema of schemas) {
    const schema = normalizeSchemaLogos(rawSchema);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    let json: string;
    try {
      json = JSON.stringify(schema);
      // Escape </ sequences that break HTML parser inside <script> tags
      json = json.replace(/<\//g, '<\\/');
    } catch {
      continue; // Skip malformed schemas
    }
    script.textContent = json;
    script.setAttribute('data-seo', 'enterprise-jsonld');
    // Organization is the sitewide identity graph node — the pruner must never
    // drop it (it was being evicted by the 6-block cap, which is why
    // logo.contentUrl was missing from every rendered page).
    if ((schema as { ['@type']?: string })['@type'] === 'Organization') {
      script.setAttribute('data-schema-pin', 'organization');
    }
    document.head.appendChild(script);
  }
}

const ORGANIZATION = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: BRAND.name,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/logo.webp`,
    contentUrl: `${SITE_URL}/logo.webp`,
    width: 512,
    height: 512,
  },
  description: BRAND.seo.description,
  email: BRAND.email.contact,
  telephone: BRAND.phone.main,
  sameAs: [
    BRAND.social.twitter,
    BRAND.social.facebook,
    BRAND.social.instagram,
    BRAND.social.github
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Casablanca',
    addressCountry: 'MA',
  },
  areaServed: [
    { '@type': 'Continent', name: 'Europe' },
    { '@type': 'Continent', name: 'North America' },
    { '@type': 'Continent', name: 'Africa' },
    { '@type': 'Continent', name: 'Asia' },
    { '@type': 'Place', name: 'Middle East' },
    { '@type': 'Continent', name: 'South America' },
    { '@type': 'Continent', name: 'Oceania' }
  ],
  knowsAbout: ['BPO', 'Call Center Outsourcing', 'Software Development', 'Digital Transformation', 'Customer Experience'],
  // no fabricated reviews, ratings or unverified credentials. Add only when real, sourced data is supplied.
};

function makeLocalBusiness(city: string, country: string, countryCode: string, lat: number, lng: number, phone?: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#local-${city.toLowerCase()}`,
    name: `${BRAND.name} — ${city}`,
    url: SITE_URL,
    telephone: phone || BRAND.phone.main,
    email: BRAND.email.contact,
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressCountry: countryCode,
    },
    geo: { '@type': 'GeoCoordinates', latitude: lat, longitude: lng },
    parentOrganization: { '@id': `${SITE_URL}/#organization` },
  };
}

const LOCAL_BUSINESSES = [
  makeLocalBusiness('Casablanca', 'Morocco', 'MA', 33.5731, -7.5898, BRAND.phone.morocco)
];

const WEBSITE = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: BRAND.name,
  url: SITE_URL,
  publisher: { '@id': `${SITE_URL}/#organization` },
  inLanguage: ['en', 'fr', 'es', 'ar', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'zh', 'ja', 'tr', 'ko', 'sv', 'da', 'hi', 'ro', 'cs', 'hu', 'fi', 'no', 'el', 'th', 'vi', 'id', 'ms', 'tl', 'bn'],
  potentialAction: [
    {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    {
      '@type': 'CommunicateAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/en/contact`,
      },
      name: 'Request a Quote',
    }
  ],
};

const SERVICES = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/#service-bpo`,
  name: 'BPO & Call Center Outsourcing',
  provider: { '@id': `${SITE_URL}/#organization` },
  serviceType: 'Business Process Outsourcing',
  areaServed: [
    { '@type': 'Continent', name: 'Europe' },
    { '@type': 'Continent', name: 'North America' },
    { '@type': 'Continent', name: 'Africa' },
    { '@type': 'Continent', name: 'Asia' },
    { '@type': 'Place', name: 'Middle East' },
    { '@type': 'Continent', name: 'South America' },
    { '@type': 'Continent', name: 'Oceania' }
  ],
  description: 'Multilingual BPO services from Casablanca, Morocco: inbound customer care, technical support, outbound sales and back-office.',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'BPO Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Inbound Customer Care' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Technical Support' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Outbound Sales & Telemarketing' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Multilingual Support' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI-Ops & Back-Office' } }
    ],
  },
};

const DIGITAL_SERVICE = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/#service-digital`,
  name: 'Digital Factory & Software Development',
  provider: { '@id': `${SITE_URL}/#organization` },
  serviceType: 'Software Development',
  areaServed: [
    { '@type': 'Continent', name: 'Europe' },
    { '@type': 'Continent', name: 'North America' },
    { '@type': 'Continent', name: 'Africa' },
    { '@type': 'Continent', name: 'Asia' },
    { '@type': 'Place', name: 'Middle East' },
    { '@type': 'Continent', name: 'South America' },
    { '@type': 'Continent', name: 'Oceania' }
  ],
  description: 'Custom SaaS development, mobile apps, AI/ML solutions, UI/UX design, and cloud infrastructure from our Digital Factory.',
};

// FAQ_PAGE removed — each page injects its own FAQPage schema to avoid Google "Duplicate FAQPage field" errors

export function EnterpriseSchemas() {
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    // Normalize path → strip language prefix to test route family
    const path = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
    const isHome = path === '/' || path === '';
    const isContactOrAbout = /^\/(contact|contact-us|about|leadership|fact-sheet)(\/|$)/.test(path);
    const isServiceHub = /^\/services(\/|$)/.test(path) && !/\/services\/.+\//.test(path); // only /services and /services/bpo, /services/digital-studio
    const isBpoHub = /^\/services\/bpo$/.test(path);
    const isDigitalHub = /^\/services\/digital-studio$/.test(path);

    const schemas: Record<string, unknown>[] = [ORGANIZATION];
    if (isHome) schemas.push(WEBSITE);
    if (isContactOrAbout) schemas.push(...LOCAL_BUSINESSES);
    // Global Service schemas only on the service hubs (not every leaf), to avoid duplication
    // with the per-page Service schema injected by BPOServiceTemplate / templates.
    if (isHome || (isServiceHub && !isBpoHub && !isDigitalHub)) {
      schemas.push(SERVICES, DIGITAL_SERVICE);
    } else if (isBpoHub) {
      schemas.push(SERVICES);
    } else if (isDigitalHub) {
      schemas.push(DIGITAL_SERVICE);
    }

    injectSchemas(schemas);
  }, [location.pathname, language]);

  return null;
}
